/**
 * Admin Login Page
 * 
 * หน้า Login สำหรับ Admin Panel
 * รองรับ username หรือ email
 * ใช้ Theme เดียวกับ Admin Panel
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getUserService } from '@/domain/user';
import { useThemeStore } from '@/stores/theme-store';
import type { AdminUser } from '@/domain/user';
import { TwoFactorAuthService } from '@/domain/two-factor-auth/two-factor-auth.service';
import { toast } from 'sonner';
import {
  Eye,
  EyeOff,
  Loader2,
  LogIn,
  AlertCircle,
  Shield,
  Smartphone,
} from 'lucide-react';

// ============================================
// Local Storage Keys
// ============================================

const STORAGE_KEYS = {
  SESSION_TOKEN: 'admin_session_token',
  USER_DATA: 'admin_user_data',
};

// ============================================
// Component
// ============================================

export function AdminLoginPage() {
  const _navigate = useNavigate();
  void _navigate // Suppress unused warning - kept for future use
  const location = useLocation();
  const { mode, colorTheme } = useThemeStore();
  
  // Form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // 2FA state
  const [show2FA, setShow2FA] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [pendingSessionToken, setPendingSessionToken] = useState<string | null>(null);
  
  // Status
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Apply theme on mount (same as App.tsx)
  useEffect(() => {
    const root = document.documentElement;
    
    // Apply mode
    if (mode === 'dark') {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
    }
    
    // Apply color theme
    root.classList.remove('theme-midnight', 'theme-ocean');
    if (colorTheme !== 'default') {
      root.classList.add(`theme-${colorTheme}`);
    }
  }, [mode, colorTheme]);
  
  // Clear error when input changes
  useEffect(() => {
    if (error) setError(null);
  }, [username, password]);
  
  // ============================================
  // Functions
  // ============================================
  
  // Note: Session check is now handled by GuestRoute wrapper
  // No need to check here anymore
  
  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    
    if (!username.trim() || !password.trim()) {
      setError('กรุณากรอกชื่อผู้ใช้และรหัสผ่าน');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('🔐 [AdminLogin] Starting login for:', username);
      
      const service = await getUserService();
      console.log('📦 [AdminLogin] UserService loaded, calling login...');
      
      const result = await service.login({
        username: username.trim(),
        password: password,
      });
      
      console.log('📥 [AdminLogin] Login result:', {
        success: result.success,
        hasUser: !!result.user,
        hasSessionToken: !!result.sessionToken,
        hasDeviceId: !!(result as any).deviceId,
        error: result.error,
      });
      
      if (result.success && result.user && result.sessionToken) {
        console.log('✅ Login success:', result.user.username);
        
        // ✅ ตรวจสอบว่า user ต้องใช้ 2FA หรือไม่
        const twoFactorCheck = await TwoFactorAuthService.checkRequirement(result.user.id);
        console.log('🔐 [2FA] Check requirement:', twoFactorCheck);
        
        // ✅ บังคับให้ Admin setup 2FA ถ้า require2FAForAdmin = true แต่ยังไม่ได้ setup
        // แต่ให้ login ได้ชั่วคราวเพื่อไป setup 2FA (grace period)
        if (twoFactorCheck.require2FAForAdmin && twoFactorCheck.isAdmin && !twoFactorCheck.isEnabled) {
          // ✅ อนุญาตให้ login ได้ชั่วคราว แต่จะต้อง setup 2FA ภายใน 24 ชั่วโมง
          // เก็บ flag ไว้ใน localStorage เพื่อแสดง warning ในแอป
          localStorage.setItem('admin_2fa_setup_required', 'true');
          localStorage.setItem('admin_2fa_setup_required_time', Date.now().toString());
          
          // Login สำเร็จ แต่จะแสดง warning ในแอป
          await completeLogin(result.sessionToken, result.user);
          
          // แสดง toast warning
          toast.warning('กรุณาตั้งค่า 2FA ภายใน 24 ชั่วโมง', {
            description: 'ระบบบังคับให้ Admin ต้องเปิดใช้งาน 2FA เพื่อความปลอดภัย',
            duration: 10000,
          });
          
          return;
        }
        
        if (twoFactorCheck.requires2FA) {
          // ต้องใช้ 2FA → แสดง 2FA step
          setPendingUserId(result.user.id);
          setPendingSessionToken(result.sessionToken);
          setShow2FA(true);
          setIsLoading(false);
          return;
        }
        
        // ไม่ต้องใช้ 2FA → login สำเร็จ
        await completeLogin(result.sessionToken, result.user);
        return;
        
      } else {
        setError(result.error || 'เข้าสู่ระบบไม่สำเร็จ');
      }
      
    } catch (err: any) {
      console.error('❌ Login error:', err);
      setError(err.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่');
    } finally {
      setIsLoading(false);
    }
  }

  async function handle2FAVerify(e: React.FormEvent) {
    e.preventDefault();
    
    if (!twoFactorCode.trim() || twoFactorCode.length !== 6) {
      setError('กรุณากรอกรหัสยืนยัน 6 หลัก');
      return;
    }
    
    if (!pendingUserId) {
      setError('ไม่พบข้อมูลผู้ใช้');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // ตรวจสอบ TOTP code
      const verifyResult = await TwoFactorAuthService.verifyToken(
        pendingUserId,
        twoFactorCode.trim()
      );
      
      if (!verifyResult.verified) {
        setError(verifyResult.error || 'รหัสยืนยันไม่ถูกต้อง');
        setIsLoading(false);
        return;
      }
      
      // 2FA ถูกต้อง → login สำเร็จ
      if (pendingSessionToken) {
        // ดึง user data อีกครั้ง (ถ้าจำเป็น)
        const service = await getUserService();
        const userResult = await service.getCurrentUser(pendingSessionToken);
        
        if (userResult) {
          await completeLogin(pendingSessionToken, userResult);
        } else {
          setError('ไม่สามารถดึงข้อมูลผู้ใช้ได้');
        }
      }
      
    } catch (err: any) {
      console.error('❌ 2FA verify error:', err);
      setError(err.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่');
    } finally {
      setIsLoading(false);
    }
  }

  async function completeLogin(sessionToken: string, user: AdminUser) {
    // Save to localStorage
    localStorage.setItem(STORAGE_KEYS.SESSION_TOKEN, sessionToken);
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
    
    // Get redirect path from location state or default to dashboard
    const from = (location.state as any)?.from?.pathname || '/dashboard';
    
    // Force page reload to reinitialize AuthProvider with new session
    window.location.href = from;
  }
  
  // ============================================
  // Render
  // ============================================
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20 p-4">
      <div className="w-full max-w-md space-y-6">
        
        {/* Logo & Title */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 mb-4">
            <Shield className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Admin Panel</h1>
          <p className="text-muted-foreground">เข้าสู่ระบบเพื่อจัดการระบบ</p>
        </div>
        
        {/* Login Form */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-foreground">
              {show2FA ? 'ยืนยันตัวตน 2 ขั้นตอน' : 'เข้าสู่ระบบ'}
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              {show2FA 
                ? 'กรอกรหัสจากแอป Authenticator'
                : 'ใช้บัญชีผู้ดูแลระบบ (super_admin)'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!show2FA ? (
              <form onSubmit={handleLogin} className="space-y-4">
              
              {/* Username/Email */}
              <div className="space-y-2">
                <Label htmlFor="username" className="text-foreground">
                  ชื่อผู้ใช้ หรือ อีเมล
                </Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="username หรือ email@example.com"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={isLoading}
                  autoComplete="username"
                  className="bg-input border-border text-foreground placeholder:text-muted-foreground focus:border-primary"
                />
              </div>
              
              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-foreground">
                  รหัสผ่าน
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    autoComplete="current-password"
                    className="bg-input border-border text-foreground placeholder:text-muted-foreground focus:border-primary pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              
              {/* Error Message */}
              {error && (
                <div className="flex items-center gap-2 p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              
              {/* Submit Button */}
              <Button 
                type="submit" 
                className="w-full gap-2" 
                disabled={isLoading || !username.trim() || !password.trim()}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    กำลังเข้าสู่ระบบ...
                  </>
                ) : (
                  <>
                    <LogIn className="h-4 w-4" />
                    เข้าสู่ระบบ
                  </>
                )}
              </Button>
              
            </form>
            ) : (
              <form onSubmit={handle2FAVerify} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="twoFactorCode" className="text-foreground">
                    รหัสยืนยัน 6 หลัก
                  </Label>
                  <Input
                    id="twoFactorCode"
                    type="text"
                    placeholder="000000"
                    value={twoFactorCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setTwoFactorCode(value);
                    }}
                    disabled={isLoading}
                    autoComplete="one-time-code"
                    className="bg-input border-border text-foreground placeholder:text-muted-foreground focus:border-primary text-center text-2xl tracking-widest font-mono"
                    maxLength={6}
                  />
                  <p className="text-xs text-muted-foreground text-center">
                    เปิดแอป Authenticator (Google Authenticator, Authy) และกรอกรหัส 6 หลัก
                  </p>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="flex items-center gap-2 p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setShow2FA(false);
                      setTwoFactorCode('');
                      setPendingUserId(null);
                      setPendingSessionToken(null);
                      setError(null);
                    }}
                    disabled={isLoading}
                  >
                    ย้อนกลับ
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 gap-2"
                    disabled={isLoading || twoFactorCode.length !== 6}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        กำลังตรวจสอบ...
                      </>
                    ) : (
                      <>
                        <Smartphone className="h-4 w-4" />
                        ยืนยัน
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
        
        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground">
          Admin Panel Pro v2.0
        </p>
        
      </div>
    </div>
  );
}

// ============================================
// Helper Hooks
// ============================================

/**
 * Get current admin user from localStorage
 */
export function getStoredUser(): AdminUser | null {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
}

/**
 * Get session token from localStorage
 */
export function getStoredSessionToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.SESSION_TOKEN);
}

/**
 * Clear stored session
 */
export function clearStoredSession(): void {
  localStorage.removeItem(STORAGE_KEYS.SESSION_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER_DATA);
}

/**
 * Check if user is logged in
 */
export function isLoggedIn(): boolean {
  return !!localStorage.getItem(STORAGE_KEYS.SESSION_TOKEN);
}

